import type { PeerUser } from "@/features/leads/api/usePeerEligibility";

/**
 * The same-role peer list behind "Transfer to peers". Shared by the peer-only
 * sheet (role has `outbound_peer` but no full outbound) and the Assign User
 * drawer's "your peers" row (role has both), so the two paths offer exactly
 * the same people: the server's peer-eligibility answer, narrowed by the
 * search box only.
 */
export default function PeerPickerList({
  peers,
  isLoading,
  error = null,
  selectedPeerId,
  onSelect,
  searchTerm = "",
}: {
  peers: PeerUser[];
  isLoading: boolean;
  /** The peer list could not be loaded (e.g. the server refused it). */
  error?: string | null;
  selectedPeerId: number | null;
  onSelect: (peerId: number) => void;
  searchTerm?: string;
}) {
  const q = searchTerm.trim().toLowerCase();
  const filtered = q
    ? peers.filter((p) => (p.name ?? "").toLowerCase().includes(q))
    : peers;

  if (error) {
    return <p className="py-4 text-[13px] text-[#9b1c31]">{error}</p>;
  }

  if (isLoading) {
    return (
      <p className="py-4 text-[13px] text-[#697571]">Loading eligible peers…</p>
    );
  }

  if (filtered.length === 0) {
    return (
      <p className="py-4 text-[13px] text-[#697571]">
        {peers.length === 0
          ? "No eligible peers — nobody on your role passes this lead's branch / custom-field rules."
          : "No peers match your search."}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {filtered.map((p) => {
        const selected = p.id === selectedPeerId;
        return (
          <button
            key={p.id}
            type="button"
            onClick={() => onSelect(p.id)}
            className={`flex items-center gap-3 rounded-[0.75rem] border p-3 text-left transition-colors ${
              selected
                ? "border-[#156548] bg-[#f0f8f4]"
                : "border-[#e5e7eb] bg-white hover:bg-[#f7f7f7]"
            }`}
          >
            <span className="flex size-9 items-center justify-center overflow-hidden rounded-full bg-[#e7efe9] text-[12px] font-medium text-[#156548]">
              {p.avatar ? (
                <img
                  src={p.avatar}
                  alt=""
                  className="size-9 rounded-full object-cover"
                />
              ) : (
                peerInitials(p.name)
              )}
            </span>
            <span className="flex flex-col">
              <span className="text-[14px] text-[#0b1a14]">
                {p.name ?? "Unknown"}
              </span>
              {p.role && (
                <span className="text-[12px] text-[#697571]">{p.role}</span>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function peerInitials(name: string | null): string {
  return (
    (name ?? "?")
      .split(" ")
      .map((w) => w[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase() || "?"
  );
}
